'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export interface UserPreferences {
  id?: string;
  userProfileId: string;
  preferredServiceTypes?: string[];
  preferredTimeSlots?: string[];
  maxBudgetPerBooking?: number;
  emailNotifications?: boolean;
  smsNotifications?: boolean;
  pushNotifications?: boolean;
  notifyBookingConfirmation?: boolean;
  notifyDriverAssigned?: boolean;
  notifyStatusUpdates?: boolean;
  notifyMessages?: boolean;
  notifyPromotions?: boolean;
  notifyReminders?: boolean;
  accessibilityRequirements?: string;
  specialHandlingPreferences?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const userPreferencesService = {
  async getPreferencesByProfileId(userProfileId: string): Promise<UserPreferences | null> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_profile_id', userProfileId)
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        if (error.code === 'PGRST116') {
          return null;
        }
        throw new Error(error.message);
      }

      return {
        id: data.id,
        userProfileId: data.user_profile_id,
        preferredServiceTypes: data.preferred_service_types,
        preferredTimeSlots: data.preferred_time_slots,
        maxBudgetPerBooking: data.max_budget_per_booking,
        emailNotifications: data.email_notifications,
        smsNotifications: data.sms_notifications,
        pushNotifications: data.push_notifications,
        notifyBookingConfirmation: data.notify_booking_confirmation,
        notifyDriverAssigned: data.notify_driver_assigned,
        notifyStatusUpdates: data.notify_status_updates,
        notifyMessages: data.notify_messages,
        notifyPromotions: data.notify_promotions,
        notifyReminders: data.notify_reminders,
        accessibilityRequirements: data.accessibility_requirements,
        specialHandlingPreferences: data.special_handling_preferences,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      throw error;
    }
  },

  async createPreferences(preferences: UserPreferences) {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .insert({
          user_profile_id: preferences.userProfileId,
          preferred_service_types: preferences.preferredServiceTypes || [],
          preferred_time_slots: preferences.preferredTimeSlots || [],
          max_budget_per_booking: preferences.maxBudgetPerBooking,
          email_notifications: preferences.emailNotifications ?? true,
          sms_notifications: preferences.smsNotifications ?? true,
          push_notifications: preferences.pushNotifications ?? true,
          notify_booking_confirmation: preferences.notifyBookingConfirmation ?? true,
          notify_driver_assigned: preferences.notifyDriverAssigned ?? true,
          notify_status_updates: preferences.notifyStatusUpdates ?? true,
          notify_messages: preferences.notifyMessages ?? true,
          notify_promotions: preferences.notifyPromotions ?? false,
          notify_reminders: preferences.notifyReminders ?? true,
          accessibility_requirements: preferences.accessibilityRequirements,
          special_handling_preferences: preferences.specialHandlingPreferences,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data;
    } catch (error: any) {
      throw error;
    }
  },

  async updatePreferences(userProfileId: string, updates: Partial<UserPreferences>) {
    const supabase = createClient();

    try {
      const updateData: any = {};

      if (updates.preferredServiceTypes !== undefined) updateData.preferred_service_types = updates.preferredServiceTypes;
      if (updates.preferredTimeSlots !== undefined) updateData.preferred_time_slots = updates.preferredTimeSlots;
      if (updates.maxBudgetPerBooking !== undefined) updateData.max_budget_per_booking = updates.maxBudgetPerBooking;
      if (updates.emailNotifications !== undefined) updateData.email_notifications = updates.emailNotifications;
      if (updates.smsNotifications !== undefined) updateData.sms_notifications = updates.smsNotifications;
      if (updates.pushNotifications !== undefined) updateData.push_notifications = updates.pushNotifications;
      if (updates.notifyBookingConfirmation !== undefined) updateData.notify_booking_confirmation = updates.notifyBookingConfirmation;
      if (updates.notifyDriverAssigned !== undefined) updateData.notify_driver_assigned = updates.notifyDriverAssigned;
      if (updates.notifyStatusUpdates !== undefined) updateData.notify_status_updates = updates.notifyStatusUpdates;
      if (updates.notifyMessages !== undefined) updateData.notify_messages = updates.notifyMessages;
      if (updates.notifyPromotions !== undefined) updateData.notify_promotions = updates.notifyPromotions;
      if (updates.notifyReminders !== undefined) updateData.notify_reminders = updates.notifyReminders;
      if (updates.accessibilityRequirements !== undefined) updateData.accessibility_requirements = updates.accessibilityRequirements;
      if (updates.specialHandlingPreferences !== undefined) updateData.special_handling_preferences = updates.specialHandlingPreferences;

      const { data, error } = await supabase
        .from('user_preferences')
        .update(updateData)
        .eq('user_profile_id', userProfileId)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data;
    } catch (error: any) {
      throw error;
    }
  },
};